import { useState, useEffect } from 'react';
import { ChevronRight, Tag, Trash2 } from 'lucide-react';
import { getSavingsHistory, getStats, clearSavings } from '../utils/savings';
import { useSite } from '../context/SiteContext';

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-MX')}`;

const formatDateLabel = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return 'Hoy';
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }).replace('.', '');
};

const MiAhorro = () => {
  const { darkMode } = useSite();
  const [history, setHistory] = useState(getSavingsHistory());
  const [stats, setStats] = useState(getStats());

  useEffect(() => {
    const refresh = () => {
      setHistory(getSavingsHistory());
      setStats(getStats());
    };
    window.addEventListener('savingsUpdated', refresh);
    window.addEventListener('storage', refresh);
    refresh();
    return () => {
      window.removeEventListener('savingsUpdated', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const cardBg = darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-neutral-200';
  const cardText = darkMode ? 'text-white' : 'text-slate-900';

  return (
    <div className={`min-h-screen pb-24 ${darkMode ? 'text-slate-100' : 'text-slate-900'}`} data-testid="mi-ahorro-page">
      {/* Header */}
      <div className="mb-4">
        <h1 className="font-fredoka text-3xl sm:text-4xl font-black" data-testid="mi-ahorro-title">Mi ahorro</h1>
        <p className={`text-sm mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          Tus cupones usados y sus descuentos
        </p>
      </div>

      {/* Big total card */}
      <div
        className="rounded-2xl p-5 sm:p-6 mb-4 flex items-center justify-between"
        style={{ backgroundColor: '#2563EB', color: '#ffffff' }}
        data-testid="mi-ahorro-total-card"
      >
        <div>
          <p className="text-xs sm:text-sm uppercase tracking-wide font-bold opacity-90">Total ahorrado</p>
          <p className="font-fredoka font-black text-4xl sm:text-5xl leading-none mt-1" data-testid="mi-ahorro-total-amount">
            {fmt(stats.total)}
          </p>
          <p className="text-sm sm:text-base mt-2 opacity-90">
            {stats.count} {stats.count === 1 ? 'cupón utilizado' : 'cupones utilizados'}
          </p>
        </div>
        <MoneyBagIcon className="w-16 h-16 sm:w-24 sm:h-24 text-white flex-shrink-0" />
      </div>

      {/* Two small cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
        <div className={`rounded-2xl border-2 p-4 sm:p-5 ${cardBg}`} data-testid="mi-ahorro-month-card">
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Este mes</p>
          <p className={`font-fredoka font-black text-2xl sm:text-3xl mt-1 ${cardText}`}>{fmt(stats.thisMonth)}</p>
        </div>
        <div className={`rounded-2xl border-2 p-4 sm:p-5 ${cardBg}`} data-testid="mi-ahorro-count-card">
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Cupones usados</p>
          <p className={`font-fredoka font-black text-2xl sm:text-3xl mt-1 ${cardText}`}>{stats.count}</p>
        </div>
      </div>

      {/* History */}
      <div className="flex items-center justify-between mb-3">
        <h2 className={`font-fredoka font-black text-2xl ${cardText}`}>Historial de cupones</h2>
        {history.length > 0 && (
          <button
            onClick={() => { if (window.confirm('¿Borrar historial de ahorro?')) clearSavings(); }}
            className={`text-xs px-2 py-1 rounded-md flex items-center gap-1 ${darkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-500 hover:bg-neutral-100'}`}
            data-testid="mi-ahorro-clear"
            title="Borrar historial"
          >
            <Trash2 className="w-3.5 h-3.5" /> Borrar
          </button>
        )}
      </div>

      {history.length === 0 ? (
        <div className={`rounded-2xl border-2 p-8 text-center ${cardBg}`} data-testid="mi-ahorro-empty">
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Aún no has usado ningún cupón. Cuando canjees uno, aparecerá aquí automáticamente.
          </p>
        </div>
      ) : (
        <div className={`rounded-2xl border-2 divide-y ${cardBg} ${darkMode ? 'divide-slate-700' : 'divide-neutral-200'}`}>
          {history.map((e) => (
            <div
              key={e.id}
              className="flex items-center gap-3 px-4 py-3.5"
              data-testid={`mi-ahorro-entry-${e.id}`}
            >
              <span className={`w-12 sm:w-14 flex-shrink-0 text-sm font-bold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                {formatDateLabel(e.date)}
              </span>
              <Tag className={`w-4 h-4 flex-shrink-0 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`} />
              <span className={`flex-1 text-sm ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                {e.type === 'bank' ? 'Cupón bancario' : 'Cupón de tienda'}
              </span>
              <span className="text-sm font-bold text-emerald-500 whitespace-nowrap">
                Descuento {fmt(e.amount)}
              </span>
              <ChevronRight className={`w-4 h-4 flex-shrink-0 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
            </div>
          ))}
        </div>
      )}

      <p className={`text-xs text-center mt-4 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        Se registra automáticamente al usar un cupón
      </p>
    </div>
  );
};

// Money bag SVG icon
const MoneyBagIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 12h20l-4 8H26z" />
    <path d="M32 20c-8 0-18 8-18 22 0 6 6 8 18 8s18-2 18-8c0-14-10-22-18-22z" />
    <text x="32" y="42" fontSize="14" textAnchor="middle" stroke="none" fill="currentColor" fontWeight="bold">$</text>
  </svg>
);

export default MiAhorro;
